import { useState, useEffect } from 'react';
import type { GachaResult } from '../stores/agentStore';
import { SummonParticles } from './SummonParticles';
import { SummonShowcase } from './SummonShowcase';
import { typeBgColor } from '../utils/agent-ui-helpers';

type SummonPhase = 'charge' | 'reveal' | 'showcase';

interface SummonAnimationProps {
  results: GachaResult[];
  wishText: string;
  onComplete: () => void;
  portraitUrls: Map<string, string>;
  currentRevealIndex: number;
}

export function SummonAnimation({
  results,
  wishText,
  onComplete,
  portraitUrls,
  currentRevealIndex,
}: SummonAnimationProps) {
  const [phase, setPhase] = useState<SummonPhase>('charge');
  const [skipped, setSkipped] = useState(false);
  const [typewriterText, setTypewriterText] = useState('');

  const currentResult = results[currentRevealIndex] ?? results[0];
  const rarity = currentResult?.agent.rarity ?? 2;
  const particleColor = results[0] ? typeBgColor(results[0].agent.type) : '#39ff14';

  // Typewriter effect for wish text during charge phase
  useEffect(() => {
    if (phase !== 'charge' || !wishText) return;
    let idx = 0;
    const interval = setInterval(() => {
      idx++;
      setTypewriterText(wishText.slice(0, idx));
      if (idx >= wishText.length) clearInterval(interval);
    }, 60);
    return () => clearInterval(interval);
  }, [phase, wishText]);

  // Phase timers
  useEffect(() => {
    if (skipped) return;

    if (phase === 'charge') {
      const timer = setTimeout(() => setPhase('reveal'), 3000);
      return () => clearTimeout(timer);
    }

    if (phase === 'reveal') {
      const timer = setTimeout(() => setPhase('showcase'), 1500);
      return () => clearTimeout(timer);
    }
  }, [phase, skipped]);

  return (
    <div className={`summon-overlay ${phase === 'reveal' && rarity >= 5 ? 'summon-overlay--shake' : ''}`}>
      <div className="summon-overlay__content">
        {phase === 'charge' && (
          <div className="summon-charge">
            <SummonParticles color={particleColor} active={true} mode="converge" intensity={0.7} />
            <div className="summon-charge__vortex" />
            {wishText && (
              <div className="summon-charge__wish-text">
                "{typewriterText}"
                <span className="summon-charge__cursor">|</span>
              </div>
            )}
            <div className="summon-charge__loading-text">
              CONNECTING TO QUANTUM NETWORK
              <span className="summon-charge__dots" />
            </div>
          </div>
        )}
        {phase === 'reveal' && (
          <div className={`summon-reveal summon-reveal--r${rarity}`}>
            <SummonParticles color={particleColor} active={true} mode="burst" intensity={rarity >= 5 ? 1.0 : 0.5} />
            <div className="summon-reveal__beam" />
            {rarity >= 4 && (
              <div className="summon-reveal__shards">
                {Array.from({ length: rarity >= 5 ? 12 : 6 }).map((_, i) => (
                  <div key={i} className="summon-reveal__shard" style={{ '--shard-i': i } as React.CSSProperties} />
                ))}
              </div>
            )}
            {rarity >= 5 && <div className="summon-reveal__flash" />}
          </div>
        )}
        {phase === 'showcase' && currentResult && (
          <>
            <SummonParticles color={particleColor} active={true} mode="ambient" intensity={0.2} />
            <SummonShowcase
              result={currentResult}
              portraitUrl={portraitUrls.get(currentResult.agent.instanceId) ?? null}
              wishText={wishText}
              onConfirm={() => {
                onComplete();
              }}
              confirmLabel="确认收下"
            />
          </>
        )}
      </div>
      {phase !== 'showcase' && (
        <button
          className="summon-overlay__skip-btn"
          onClick={() => {
            setSkipped(true);
            setPhase('showcase');
          }}
        >
          跳过 »
        </button>
      )}
    </div>
  );
}
